import React, { useEffect, useState } from "react";
import { api, fileUrl } from "./api";
import "./DesignModule.css";

const DesignModule = ({ project }) => {
  const projectId = project?._id || project?.id;

  const [data, setData] = useState(null);
  const [openFolder, setOpenFolder] = useState(null);
  const [uploadingFolderId, setUploadingFolderId] = useState(null);

  const getCurrentUserName = () => {
    try {
      const userData = localStorage.getItem("user");
      const parsedUser = userData ? JSON.parse(userData) : null;

      return (
        parsedUser?.name ||
        parsedUser?.userName ||
        localStorage.getItem("userName") ||
        localStorage.getItem("name") ||
        "Unknown User"
      );
    } catch {
      return (
        localStorage.getItem("userName") ||
        localStorage.getItem("name") ||
        "Unknown User"
      );
    }
  };

  const loadDesign = async () => {
    if (!projectId) return;

    try {
      const res = await api.get(`/drawing-design/${projectId}`);
      setData(res.data);

      const firstOpen = res.data.folders?.find((f) => f.isOpen);
      setOpenFolder(firstOpen?._id || res.data.folders?.[0]?._id || null);
    } catch (error) {
      console.error("Load design error:", error);
    }
  };

  useEffect(() => {
    loadDesign();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const uploadFiles = async (folderId, files) => {
    if (!projectId) {
      alert("Project ID missing");
      return;
    }

    if (!files || files.length === 0) return;

    try {
      setUploadingFolderId(folderId);

      const formData = new FormData();

      Array.from(files).forEach((file) => {
        formData.append("files", file);
      });

      formData.append("uploadedBy", getCurrentUserName());

      const res = await api.post(
        `/drawing-design/${projectId}/folders/${folderId}/upload`,
        formData,
      );

      setData(res.data);
      setOpenFolder(folderId);
    } catch (error) {
      console.error("Upload file error:", error);
      alert("File upload failed");
    } finally {
      setUploadingFolderId(null);
    }
  };

  if (!data) return <div style={styles.loading}>Loading Design...</div>;

  return (
    <div className="dm-page" style={styles.page}>
      <div className="dm-top" style={styles.topBar}>
        <div>
          <h3 style={styles.title}>Design Files</h3>
          <p style={styles.subtitle}>
            Manage all design drawings, renders, MOM and material files
          </p>
        </div>

        <div style={styles.totalBadge}>
          Total Files:{" "}
          {data.folders?.reduce(
            (total, folder) => total + (folder.files?.length || 0),
            0,
          )}
        </div>
      </div>

      <div className="dm-tabs-wrap" style={styles.tabsWrap}>
        <div className="dm-tabs" style={styles.tabs}>
          {data.folders?.map((folder) => {
            const isActive = openFolder === folder._id;

            return (
              <button
                key={folder._id}
                onClick={() => setOpenFolder(folder._id)}
                style={{
                  ...styles.tab,
                  ...(isActive ? styles.activeTab : {}),
                }}
              >
                <span>{folder.folderName}</span>
                <span
                  style={{
                    ...styles.count,
                    ...(isActive ? styles.activeCount : {}),
                  }}
                >
                  {folder.files?.length || 0}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="dm-table-wrap" style={styles.tableWrap}>
        <table className="dm-table" style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Name of File</th>
              <th style={styles.th}>Type of File</th>
              <th style={styles.th}>Uploaded By</th>
              <th style={styles.th}>Uploaded On</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Actions</th>
            </tr>
          </thead>

          <tbody>
            {data.folders?.map((folder) => (
              <React.Fragment key={folder._id}>
                <tr className="dm-folder-row" style={styles.folderRow}>
                  <td
                    className="dm-folder-cell"
                    colSpan="6"
                    style={styles.folderCell}
                  >
                    <button
                      onClick={() => setOpenFolder(folder._id)}
                      style={styles.arrowBtn}
                    >
                      {openFolder === folder._id ? "⌄" : "›"}
                    </button>

                    <div style={{ flex: 1 }}>
                      <div style={styles.folderName}>{folder.folderName}</div>
                      <div style={styles.folderMeta}>
                        {folder.files?.length || 0} file uploaded
                      </div>
                    </div>

                    <label
                      className="dm-upload"
                      style={{
                        ...styles.uploadBtn,
                        opacity: uploadingFolderId === folder._id ? 0.6 : 1,
                        pointerEvents:
                          uploadingFolderId === folder._id ? "none" : "auto",
                      }}
                    >
                      {uploadingFolderId === folder._id
                        ? "Uploading..."
                        : "+ Upload"}

                      <input
                        type="file"
                        multiple
                        style={{ display: "none" }}
                        onChange={(e) => {
                          uploadFiles(folder._id, e.target.files);
                          e.target.value = "";
                        }}
                      />
                    </label>
                  </td>
                </tr>

                {openFolder === folder._id &&
                  folder.files?.map((file) => (
                    <tr
                      key={file._id}
                      className="dm-file-row"
                      style={styles.fileRow}
                    >
                      <td className="dm-c-name" style={styles.td}>
                        <a
                          href={fileUrl(file.fileUrl)}
                          target="_blank"
                          rel="noreferrer"
                          style={styles.fileLink}
                        >
                          <span style={styles.fileIcon}>📄</span>
                          {file.fileName}
                        </a>
                      </td>

                      <td data-label="Type of File" style={styles.td}>
                        <span style={styles.typeBadge}>
                          {file.fileType || "-"}
                        </span>
                      </td>

                      <td data-label="Uploaded By" style={styles.td}>
                        <span style={styles.userText}>
                          {file.uploadedBy || "Unknown User"}
                        </span>
                      </td>

                      <td data-label="Uploaded On" style={styles.td}>
                        {file.uploadedOn
                          ? new Date(file.uploadedOn).toLocaleString()
                          : "-"}
                      </td>

                      <td data-label="Status" style={styles.td}>
                        <span style={styles.status}>
                          {file.status || "Draft"}
                        </span>
                      </td>

                      <td className="dm-c-actions" style={styles.td}>
                        <button style={styles.actionBtn}>⋮</button>
                      </td>
                    </tr>
                  ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const styles = {
  page: {
    background: "#ffffff",
    border: "1px solid #99f6e4",
    borderRadius: "20px",
    overflow: "hidden",
    boxShadow: "0 12px 30px rgba(15,118,110,0.10)",
  },

  loading: {
    padding: 30,
    color: "#0f766e",
    fontWeight: 800,
  },

  topBar: {
    padding: "20px 22px",
    background: "linear-gradient(90deg, #f0fdfa, #ffffff)",
    borderBottom: "1px solid #99f6e4",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 16,
    flexWrap: "wrap",
  },

  title: {
    margin: 0,
    fontSize: 22,
    fontWeight: 900,
    color: "#134e4a",
  },

  subtitle: {
    margin: "5px 0 0",
    fontSize: 14,
    color: "#0f766e",
    fontWeight: 600,
  },

  totalBadge: {
    padding: "9px 14px",
    borderRadius: "999px",
    background: "#ccfbf1",
    color: "#0f766e",
    fontSize: 13,
    fontWeight: 900,
  },

  tabsWrap: {
    background: "#ffffff",
    borderBottom: "1px solid #99f6e4",
    padding: "12px 14px",
  },

  tabs: {
    display: "flex",
    gap: "10px",
    overflowX: "auto",
    paddingBottom: "4px",
    scrollbarWidth: "thin",
  },

  tab: {
    border: "1px solid #99f6e4",
    background: "#ffffff",
    padding: "10px 15px",
    minHeight: 42,
    borderRadius: "999px",
    cursor: "pointer",
    whiteSpace: "nowrap",
    fontSize: "14px",
    fontWeight: 800,
    color: "#134e4a",
    display: "flex",
    alignItems: "center",
    gap: 8,
    boxShadow: "0 4px 12px rgba(15,118,110,0.08)",
  },

  activeTab: {
    background: "#0f766e",
    color: "#ffffff",
    border: "1px solid #0f766e",
  },

  count: {
    background: "#ccfbf1",
    color: "#134e4a",
    borderRadius: "999px",
    padding: "2px 8px",
    fontSize: "12px",
    fontWeight: 900,
  },

  activeCount: {
    background: "#ffffff",
    color: "#0f766e",
  },

  tableWrap: {
    width: "100%",
    overflowX: "auto",
  },

  table: {
    width: "100%",
    minWidth: "1050px",
    borderCollapse: "separate",
    borderSpacing: 0,
    fontSize: 14,
  },

  th: {
    textAlign: "left",
    padding: "15px 16px",
    background: "#f0fdfa",
    borderBottom: "1px solid #99f6e4",
    color: "#115e59",
    fontSize: "12px",
    fontWeight: 900,
    textTransform: "uppercase",
    letterSpacing: "0.4px",
  },

  td: {
    padding: "15px 16px",
    borderBottom: "1px solid #ccfbf1",
    color: "#134e4a",
    verticalAlign: "middle",
  },

  folderRow: {
    background: "#ffffff",
  },

  folderCell: {
    padding: "16px",
    borderBottom: "1px solid #ccfbf1",
    display: "flex",
    alignItems: "center",
    gap: 12,
    background: "#ffffff",
  },

  folderName: {
    color: "#134e4a",
    fontSize: "15px",
    fontWeight: 900,
  },

  folderMeta: {
    marginTop: 3,
    color: "#0f766e",
    fontSize: "12px",
    fontWeight: 700,
  },

  arrowBtn: {
    width: 34,
    height: 34,
    border: "1px solid #99f6e4",
    background: "#f0fdfa",
    borderRadius: "10px",
    fontSize: 20,
    cursor: "pointer",
    color: "#0f766e",
  },

  uploadBtn: {
    color: "#ffffff",
    background: "#0f766e",
    cursor: "pointer",
    fontWeight: 900,
    padding: "9px 14px",
    borderRadius: "12px",
    fontSize: "13px",
    boxShadow: "0 6px 14px rgba(15,118,110,0.25)",
  },

  fileRow: {
    background: "#ffffff",
  },

  fileLink: {
    color: "#0f766e",
    textDecoration: "none",
    fontWeight: 800,
    display: "flex",
    alignItems: "center",
    gap: 8,
  },

  fileIcon: {
    width: 30,
    height: 30,
    borderRadius: "10px",
    background: "#f0fdfa",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },

  typeBadge: {
    background: "#f0fdfa",
    color: "#115e59",
    padding: "5px 10px",
    borderRadius: "999px",
    fontSize: "12px",
    fontWeight: 800,
  },

  userText: {
    fontWeight: 800,
    color: "#134e4a",
  },

  status: {
    color: "#0f766e",
    background: "#f0fdfa",
    border: "1px solid #5eead4",
    borderRadius: "999px",
    padding: "5px 10px",
    fontSize: "12px",
    fontWeight: 900,
  },

  actionBtn: {
    width: 34,
    height: 34,
    borderRadius: "10px",
    border: "1px solid #99f6e4",
    background: "#ffffff",
    cursor: "pointer",
    fontSize: "18px",
    color: "#0f766e",
  },
};

export default DesignModule;
